/**
 * Protocol-v2 send pipeline + durable outbox (FR-MSG-001, NET-REL-002, SPEC §12).
 *
 *   app payload (id, type, body) ──► session record (AKE / Double Ratchet)
 *        ──► sealed envelope (hybrid KEM to recipient) ──► kind-1059 event
 *            signed by a throwaway key ──► per-recipient outbox row ──► publish
 *
 * The signed event is stored and republished VERBATIM by the retry loop; it is
 * never re-encrypted, so retries cannot burn ratchet keys or create duplicates.
 */
import { ephemeralEventSigner, fromHex, sha256ConversationId, toHex } from "@veto/crypto-bridge";
import type { Account, Contact, PublishOutcome, TransportChannel } from "@veto/domain";
import { createEventId, nextRetryAt } from "@veto/domain";
import {
  buildSealedTransportEvent,
  encodeAppPayload,
  transportRoutingHint,
  type AppPayload,
  type SignedTransportEvent,
} from "@veto/protocol";
import {
  deleteOutbox,
  getContact,
  listDueOutbox,
  listOutboxForMessage,
  putOutbox,
  updateMessageDelivery,
  updateOutboxStatus,
  type OutboxRecord,
} from "@veto/storage";
import type { DirectPeerLink, RelayPool } from "@veto/transport";
import { getSupabaseTransport, recipientHint, recipientRealtimeTopic } from "./fallbackTransport";
import type { SessionStore } from "./sessionStore";

export type SendContext = {
  store: SessionStore;
  account: Account;
  relay: RelayPool;
  peers: Map<string, DirectPeerLink>;
};

/** Pairwise conversation id (sha256 of both identity keys — unchanged from v1). */
export async function directConversationId(selfEdHex: string, peerEdHex: string): Promise<string> {
  return toHex(await sha256ConversationId(fromHex(selfEdHex), fromHex(peerEdHex)));
}

export function peerKeyBytes(contact: Contact): { ed: Uint8Array; hybrid: Uint8Array } {
  return { ed: fromHex(contact.messagingPublicKeyHex), hybrid: fromHex(contact.hybridPublicKeyHex) };
}

export function newPayload(t: number, body: string, id = createEventId(), at = Date.now()): AppPayload {
  return { v: 1, t, id, at, b: body };
}

/** Encrypt + seal one payload for one contact → signed transport event. */
export async function sealPayloadFor(
  ctx: SendContext,
  contact: Contact,
  payload: AppPayload,
): Promise<SignedTransportEvent> {
  const convId = await directConversationId(ctx.account.messagingPublicKeyHex, contact.messagingPublicKeyHex);
  const envelope = await ctx.store.encrypt(convId, peerKeyBytes(contact), encodeAppPayload(payload));
  return buildSealedTransportEvent({
    envelope,
    recipientHint: transportRoutingHint(contact.messagingPublicKeyHex),
    recipientPubkeyHex: contact.transportPublicKeyHex,
    createdAtMs: payload.at,
    signer: ephemeralEventSigner(),
  });
}

/**
 * Publish one already-signed event toward `contact` (SPEC §10/§12):
 *   direct peer (no ack) → rendezvous + healthiest relays → Supabase last resort.
 * "backed_up" requires ≥2 independent relay acknowledgements (NET-REL-002).
 */
export async function publishEvent(
  ctx: Pick<SendContext, "relay" | "peers" | "account">,
  event: SignedTransportEvent,
  contact: Contact,
): Promise<PublishOutcome> {
  const convId = await directConversationId(ctx.account.messagingPublicKeyHex, contact.messagingPublicKeyHex);
  const json = JSON.stringify(event);
  let direct = false;
  const link = ctx.peers.get(convId);
  if (link?.state === "open") {
    try {
      link.send(json);
      direct = true;
    } catch {
      direct = false;
    }
  }
  const res = await ctx.relay.publish(event, { routingKey: recipientHint(contact) });
  let acks = res.acks;
  let transport: TransportChannel | undefined =
    res.privateAcks > 0 ? "relay" : res.communityAcks > 0 ? "community" : undefined;
  if (acks === 0) {
    const rt = getSupabaseTransport();
    if (rt) {
      const ok = await rt.publish(recipientRealtimeTopic(contact), json).catch(() => false);
      if (ok) {
        acks = 1;
        transport = "realtime";
      }
    }
  }
  if (direct) transport = acks > 0 ? "direct+relay" : "direct";
  const status: PublishOutcome["status"] = acks >= 2 ? "backed_up" : acks >= 1 || direct ? "sent" : "queued";
  return { status, transport };
}

function rank(s: PublishOutcome["status"]): number {
  return s === "backed_up" ? 2 : s === "sent" ? 1 : 0;
}

/**
 * Seal `payload` for every recipient, commit each sealed event to the durable
 * outbox, then publish in parallel. Returns the aggregate delivery outcome
 * (backed_up only when every recipient's copy is backed up).
 */
export async function sendPayload(
  ctx: SendContext,
  opts: {
    /** UI conversation (group or direct) the message belongs to. */
    conversationId: string;
    recipients: Contact[];
    payload: AppPayload;
    /** true when a UI message row tracks this send (status aggregation). */
    tracksMessage: boolean;
  },
): Promise<PublishOutcome> {
  const recipients = opts.recipients.filter(
    (c) => !c.blockedAt && c.messagingPublicKeyHex !== ctx.account.messagingPublicKeyHex,
  );
  if (recipients.length === 0) return { status: "queued" };
  const sealed: { contact: Contact; event: SignedTransportEvent; row: OutboxRecord }[] = [];
  for (const contact of recipients) {
    const event = await sealPayloadFor(ctx, contact, opts.payload);
    const row: OutboxRecord = {
      id: `${opts.payload.id}:${contact.id}`,
      messageEventId: opts.payload.id,
      conversationId: opts.conversationId,
      recipientId: contact.id,
      eventJson: JSON.stringify(event),
      createdAt: opts.payload.at,
      fanout: recipients.length,
      attempts: 0,
      // The immediate publish below owns the first attempt; the loop takes over after.
      nextAt: Date.now() + 20_000,
      status: "queued",
      tracksMessage: opts.tracksMessage,
    };
    await putOutbox(row);
    sealed.push({ contact, event, row });
  }
  const outcomes = await Promise.all(
    sealed.map(async ({ contact, event, row }) => {
      let out: PublishOutcome = { status: "queued" };
      try {
        out = await publishEvent(ctx, event, contact);
      } catch {
        /* stays queued — retry loop */
      }
      await settleOutboxRow(row, out, 1);
      return out;
    }),
  );
  const worst = outcomes.reduce((a, b) => (rank(b.status) < rank(a.status) ? b : a));
  const best = outcomes.reduce((a, b) => (rank(b.status) > rank(a.status) ? b : a));
  // Aggregate: backed_up only if all are; otherwise "sent" once anyone has it.
  const status: PublishOutcome["status"] =
    rank(worst.status) === 2 ? "backed_up" : rank(best.status) >= 1 ? "sent" : "queued";
  return { status, transport: best.transport };
}

async function settleOutboxRow(row: OutboxRecord, out: PublishOutcome, attempts: number): Promise<void> {
  if (out.status === "backed_up") {
    await deleteOutbox(row.id);
    return;
  }
  await updateOutboxStatus(row.id, {
    attempts,
    status: rank(out.status) > rank(row.status) ? out.status : row.status,
    transport: out.transport ?? row.transport,
    nextAt: retryAt(attempts, row.createdAt),
  });
}

/** Retry schedule: SPEC §12 steps, then every 12 h while the relay TTL lasts. */
function retryAt(attempts: number, _createdAt: number, now = Date.now()): number {
  return nextRetryAt(attempts, now);
}

/** Relays keep undelivered events ~30 days; stop retrying beyond that (NET-REL-004). */
const OUTBOX_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/** Recompute a UI message's delivery state from its remaining outbox rows. */
async function aggregateMessageStatus(messageEventId: string, fanout: number): Promise<PublishOutcome["status"]> {
  const remaining = await listOutboxForMessage(messageEventId);
  if (remaining.length === 0) return "backed_up";
  if (remaining.length < fanout || remaining.some((r) => r.status !== "queued")) return "sent";
  return "queued";
}

/**
 * Durable retry loop over the wire outbox (FR-MSG-001). Republishes stored
 * events verbatim with an in-flight guard; reports UI status changes.
 */
export function startOutboxLoop(
  ctx: () => Pick<SendContext, "relay" | "peers" | "account"> | null,
  onMessageStatus: (u: { messageEventId: string; conversationId: string } & PublishOutcome) => void,
  intervalMs = 4_000,
): () => void {
  const inflight = new Set<string>();
  let stopped = false;
  const tick = async () => {
    const c = ctx();
    if (!c || stopped) return;
    let due: OutboxRecord[];
    try {
      due = await listDueOutbox(Date.now(), 25);
    } catch {
      return;
    }
    await Promise.all(
      due.map(async (row) => {
        if (inflight.has(row.id)) return;
        inflight.add(row.id);
        try {
          if (Date.now() - row.createdAt > OUTBOX_MAX_AGE_MS) {
            await deleteOutbox(row.id);
            if (row.tracksMessage) {
              await updateMessageDelivery(row.messageEventId, { status: "failed" });
              onMessageStatus({ messageEventId: row.messageEventId, conversationId: row.conversationId, status: "queued" });
            }
            return;
          }
          const contact = await getContact(row.recipientId);
          if (!contact || contact.blockedAt) {
            await deleteOutbox(row.id);
            return;
          }
          let out: PublishOutcome = { status: "queued" };
          try {
            out = await publishEvent(c, JSON.parse(row.eventJson) as SignedTransportEvent, contact);
          } catch {
            /* keep queued */
          }
          await settleOutboxRow(row, out, row.attempts + 1);
          if (row.tracksMessage && out.status !== "queued") {
            const status = await aggregateMessageStatus(row.messageEventId, row.fanout);
            await updateMessageDelivery(row.messageEventId, { status, transport: out.transport });
            onMessageStatus({
              messageEventId: row.messageEventId,
              conversationId: row.conversationId,
              status,
              transport: out.transport,
            });
          }
        } finally {
          inflight.delete(row.id);
        }
      }),
    );
  };
  const id = window.setInterval(() => void tick(), intervalMs);
  void tick();
  return () => {
    stopped = true;
    window.clearInterval(id);
  };
}
